import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { faAngleDown, faAngleRight, faEye, faHome, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { menuItemInterface } from 'src/app/util/interfaces/menuItem';
import { PermissionService } from 'src/app/util/services/permission/permission.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DotBounceLoadingEffectModule } from 'src/app/page_component_item/dot-bounce-loading-effect/dot-bounce-loading-effect.module';
import { SessionStatus } from 'src/app/services/profile/sessionStatus.enum';
import { NotificationPageService } from 'src/app/page/notification-page/service/notification-page.service';
import { RouterModule } from '@angular/router';
import { MenuItems } from './data/menu.items';
import { GlobalService } from 'src/app/services/global/global.service';
import { MenuLateralService } from './data/menu-lateral.service';

@Component({
	selector: 'app-menu-lateral',
	standalone: true,
	imports: [CommonModule, RouterModule, FaIconComponent, DotBounceLoadingEffectModule],
	templateUrl: './menu-lateral.component.html',
	styleUrl: './menu-lateral.component.scss',
})
export class MenuLateralComponent implements OnInit {
	private readonly permissionService = inject(PermissionService);
	protected readonly profileService = inject(ProfileService);
	protected readonly menuLateralService = inject(MenuLateralService);
	protected readonly notificationPageService = inject(NotificationPageService);
	protected readonly globalService = inject(GlobalService);

	protected SessionStatus = SessionStatus;
	protected readonly icons = {
		faEye: faEye,
		faSignOutAlt: faSignOutAlt,
		faAngleDown: faAngleDown,
		faAngleRight: faAngleRight,
	};

	@Input() labels: { [key: string]: string } = {};

	mainMenuItem: menuItemInterface;
	menuArray: menuItemInterface[];

	isAllNotificationsReaded = true;

	viewMode = this.menuLateralService.menuLateralViewMode;
	isExpanded = () => {
		if (this.viewMode() === 'alwaysOpen') return true;
		if (this.viewMode() === 'alwaysClosed') return false;
		return this.menuLateralService.isMenuExpanded();
	};

	@Output() disconnect = new EventEmitter<void>();

	ngOnInit(): void {
		const temporaryMenu = [];
		for (const menuItem of MenuItems) {
			if (
				(menuItem.isAnalystView && this.profileService.userData().sessionStatus == SessionStatus.ENDUSER) ||
				!this.permissionService.hasPermissions(menuItem.permissions.join(','))
			) {
				continue;
			}
			if (menuItem.children?.length > 0) {
				const temporaryChildren = [];
				for (let index = 0; index < menuItem.children.length; index++) {
					const childItem = menuItem.children[index];
					if (
						(childItem.isAnalystView && this.profileService.userData().sessionStatus == SessionStatus.ENDUSER) ||
						!this.permissionService.hasPermissions(childItem.permissions.join(','))
					) {
						continue;
					}
					temporaryChildren.push(childItem);
				}
				menuItem.children = temporaryChildren;
			}
			if (menuItem.routerLink == this.profileService.userData().main_menu) {
				this.mainMenuItem = menuItem;
				this.mainMenuItem.icon = faHome;
			} else {
				temporaryMenu.push(menuItem);
			}
		}
		this.menuArray = temporaryMenu;
		if (this.mainMenuItem) {
			this.menuArray.unshift(this.mainMenuItem);
		}
		this.menuArray.at(-1).onClick = () => {
			this.disconnect.emit();
		};

		this.notificationPageService.getStorageNotification().subscribe({
			next: (items) => {
				const storedItems = localStorage.getItem('notifications');
				const notifications: { id: string; read: boolean; customer: string }[] = storedItems ? JSON.parse(storedItems) : [];

				for (const item of items) {
					const exists = notifications.some((notification) => notification.id === item.id);

					if (!exists) {
						notifications.push({ id: item.id, read: false, customer: item.customer });
					}
				}

				localStorage.setItem('notifications', JSON.stringify(notifications));
			},
			complete: () => {
				const storedItems = localStorage.getItem('notifications');
				const notifications: { id: string; read: boolean; customer: string }[] = storedItems ? JSON.parse(storedItems) : [];

				for (const notification of notifications) {
					if (
						!notification.read &&
						(notification.customer == '' || notification.customer?.toString() == this.profileService.userData().idCustomer)
					) {
						this.isAllNotificationsReaded = false;
					}
				}
			},
		});
	}

	expandMenu() {
		if (this.viewMode() === 'alwaysClosed' || this.viewMode() === 'alwaysOpen' || this.viewMode() === 'hamburgerOpen') return;

		if (this.viewMode() === 'hoverOpen') {
			this.menuLateralService.isMenuExpanded.set(true);
		}
	}

	collapseMenu() {
		if (this.viewMode() === 'alwaysClosed' || this.viewMode() === 'alwaysOpen' || this.viewMode() === 'hamburgerOpen') return;

		if (this.viewMode() === 'hoverOpen') {
			this.menuLateralService.isMenuExpanded.set(false);
		}
	}

	expandMenuItem(menuItem: menuItemInterface) {
		if (menuItem.children?.length === 0) return;
		menuItem.expanded = !menuItem.expanded;
	}
}
